import { useEffect, useState } from "react";
import { useInView } from "@/lib/useInView";

/**
 * Animated count-up. Accepts a number OR a label string containing digits
 * (e.g. "07", "16+", "24/7"). Animates the FIRST numeric run and preserves
 * padding + surrounding characters.
 */
export default function CountUp({
  value,
  duration = 1400,
  className = "",
  testId,
}) {
  const [ref, visible] = useInView({ threshold: 0.4 });

  const isNumber = typeof value === "number";
  const source = isNumber ? String(value) : String(value);
  const match = source.match(/\d+/);
  const firstNum = match ? match[0] : "";
  const target = firstNum ? parseInt(firstNum, 10) : 0;

  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!visible) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, target, duration]);

  let rendered;
  if (!firstNum) {
    rendered = source;
  } else {
    const padded = display.toString().padStart(firstNum.length, "0");
    rendered = source.replace(firstNum, padded);
  }

  return (
    <span ref={ref} data-testid={testId} className={className}>
      {rendered}
    </span>
  );
}
